import { createSlice } from "@reduxjs/toolkit";
import { makeThunk } from "helpers/makeThunk";
import { handleCommonCases } from "helpers/thunkHandler";
import {
  ADD_ADDONS,
  ADD_MEMBERSHIP_PLAN,
  DELETE_ADDON,
  DELETE_MEMBERSHIP_PLAN,
  FREE_WASH_RULES,
  GET_ADDONS,
  GET_ADDONS_DETAILS,
  GET_ADDONS_LIST,
  GET_MEMBERSHIP_PLAN_DETAILS,
  GET_MEMBERSHIP_PLAN_LIST,
  GET_MEMBERSHIP_PLANS,
  UPDATE_ADDON,
  UPDATE_ADDONS_STATUS,
  UPDATE_FREE_WASH_RULES,
  UPDATE_MEMBERSHIP_PLAN,
  UPDATE_MEMBERSHIP_STATUS,
} from "helpers/url_helper";

export const getPlans = makeThunk({ url: GET_MEMBERSHIP_PLANS });
export const addPlan = makeThunk({
  url: ADD_MEMBERSHIP_PLAN,
  method: "formDataPost",
});
export const updatePlan = makeThunk({
  url: UPDATE_MEMBERSHIP_PLAN,
  method: "formDataPut",
});
export const deletePlan = makeThunk({
  url: DELETE_MEMBERSHIP_PLAN,
  method: "delete",
});
export const updatePlanStatus = makeThunk({
  url: UPDATE_MEMBERSHIP_STATUS,
  method: "patch",
});
export const getPlanDetails = makeThunk({
  type: `${GET_MEMBERSHIP_PLAN_DETAILS}_GET_DETAILS`,
  url: (id) => `${GET_MEMBERSHIP_PLAN_DETAILS}/${id}`,
  method: "get",
});
export const getPlansList = makeThunk({
  type: `${GET_MEMBERSHIP_PLAN_LIST}_GET_DETAILS`,
  url: (payload) => {
    const planType = payload?.planType;
    return planType
      ? `${GET_MEMBERSHIP_PLAN_LIST}?planType=${planType}`
      : GET_MEMBERSHIP_PLAN_LIST;
  },
  method: "get",
});

export const getAddons = makeThunk({ url: GET_ADDONS });
export const addAddon = makeThunk({ url: ADD_ADDONS });
export const updateAddon = makeThunk({ url: UPDATE_ADDON, method: "put" });
export const getAddonsList = makeThunk({ url: GET_ADDONS_LIST, method: "get" });
export const deleteAddon = makeThunk({ url: DELETE_ADDON, method: "delete" });
export const updateAddonStatus = makeThunk({
  url: UPDATE_ADDONS_STATUS,
  method: "patch",
});
export const getAddonDetails = makeThunk({
  url: (id) => `${GET_ADDONS_DETAILS}/${id}`,
  method: "get",
});

export const getFreeWashRules = makeThunk({
  url: FREE_WASH_RULES,
  type: FREE_WASH_RULES + "GET_DETAILS",
  method: "get",
});
export const updateFreeWashRule = makeThunk({
  url: UPDATE_FREE_WASH_RULES,
  type: UPDATE_FREE_WASH_RULES + "UPDATE_RULES",
  method: "put",
});

const initialState = {
  loading: false,
  isLoading: false,
  isDeleting: false,
  loadingItemId: null,
  totalCount: null,
  totalPages: null,
  currentPage: null,
  planFilters: {
    perPage: 50,
    pageNo: 1,
    status: -1,
    orderBy: -1,
    keyword: "",
    dates: [],
    planType: "",
  },
  addonFilters: {
    perPage: 50,
    pageNo: 1,
    status: -1,
    orderBy: -1,
    keyword: "",
    dates: [],
  },
  plans: [],
  plansList: [],
  planDetails: {},
  addons: [],
  addonsList: [],
  addonDetails: {},
  freeWashRules: {},
};

const membershipSlice = createSlice({
  name: "membership",
  initialState,

  reducers: {
    setPlanFilter: (state, action) => {
      const { key, value } = action.payload;
      state.planFilters[key] = value;
    },

    resetPlanFilters: (state) => {
      state.planFilters = initialState.planFilters;
    },
    setAddonFilter: (state, action) => {
      const { key, value } = action.payload;
      state.addonFilters[key] = value;
    },

    resetAddonFilters: (state) => {
      state.addonFilters = initialState.addonFilters;
    },
  },

  extraReducers: (builder) => {
    // MEMBERSHIP PLANS
    handleCommonCases(builder, getPlans, {
      onFulfilled: (state, action) => {
        const { data, totalCount, totalPages, currentPage } =
          action.payload || {};
        state.plans = data || [];
        state.totalCount = totalCount;
        state.totalPages = totalPages;
        state.currentPage = currentPage;
      },
    });

    handleCommonCases(builder, updatePlanStatus, {
      loadingKey: "isLoading",
      onPending: (state, action) => {
        state.loadingItemId = action.meta.arg._id;
      },
      onFulfilled: (state, action) => {
        const { id, status } = action.meta.arg;
        const plan = state.plans.find((item) => item._id === id);
        if (plan) {
          plan.status = status;
        }
        state.loadingItemId = null;
      },
      onRejected: (state) => {
        state.loadingItemId = null;
      },
    });

    handleCommonCases(builder, addPlan, {
      loadingKey: "isLoading",
    });

    handleCommonCases(builder, getPlansList, {
      loadingKey: "isLoading",
      onFulfilled: (state, action) => {
        const { data } = action.payload || {};
        state.plansList = data || [];
      },
    });

    handleCommonCases(builder, getPlanDetails, {
      onPending: (state) => {
        state.planDetails = {};
      },
      onFulfilled: (state, action) => {
        const { data } = action.payload || {};
        state.planDetails = data || {};
      },
    });

    handleCommonCases(builder, deletePlan, {
      loadingKey: "isDeleting",
      onFulfilled: (state, action) => {
        const deletedId = action.meta.arg.id;
        state.plans = state.plans.filter((item) => item._id !== deletedId);
        if (state.totalCount !== null) {
          state.totalCount = state.totalCount - 1;
        }
      },
    });

    // MEMBERSHIP ADDONS
    handleCommonCases(builder, getAddons, {
      onFulfilled: (state, action) => {
        const { data, totalCount, totalPages, currentPage } =
          action.payload || {};
        state.addons = data || [];
        state.totalCount = totalCount;
        state.totalPages = totalPages;
        state.currentPage = currentPage;
      },
    });

    handleCommonCases(builder, getAddonsList, {
      onFulfilled: (state, action) => {
        const { data } = action.payload || {};
        state.addonsList = data || [];
      },
    });

    handleCommonCases(builder, updateAddonStatus, {
      loadingKey: "isLoading",
      onPending: (state, action) => {
        state.loadingItemId = action.meta.arg._id;
      },
      onFulfilled: (state, action) => {
        const { id, status } = action.meta.arg;
        const addon = state.addons.find((item) => item._id === id);
        if (addon) {
          addon.status = status;
        }
        state.loadingItemId = null;
      },
      onRejected: (state) => {
        state.loadingItemId = null;
      },
    });

    handleCommonCases(builder, addAddon, {
      loadingKey: "isLoading",
    });

    handleCommonCases(builder, deleteAddon, {
      loadingKey: "isDeleting",
      onFulfilled: (state, action) => {
        const deletedId = action.meta.arg.id;
        state.addons = state.addons.filter((item) => item._id !== deletedId);
      },
    });

    handleCommonCases(builder, getAddonDetails, {
      onPending: (state) => {
        state.addonDetails = {};
      },
      onFulfilled: (state, action) => {
        const { data } = action.payload || {};
        state.addonDetails = data || {};
      },
    });

    handleCommonCases(builder, getFreeWashRules, {
      onFulfilled: (state, action) => {
        const { data } = action.payload || {};
        state.freeWashRules = data || {};
      },
    });
    handleCommonCases(builder, updateFreeWashRule, {
      loadingKey: "isLoading",
    });
  },
});

export const {
  setAddonFilter,
  resetAddonFilters,
  setPlanFilter,
  resetPlanFilters,
} = membershipSlice.actions;

export default membershipSlice.reducer;
